import React, { useCallback, useState } from 'react';
import styled from 'styled-components';

import { FjCheckbox, FjInput, FjStyles, fjModalSizes } from 'components/primitives';
import { Modal, Button } from 'components/styled-primitives';
import { validateEmail } from 'utils/validators';
import { navigate } from 'utils';

interface FormState {
  acknowledge: boolean;
  company: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
  phone: string;
}

const Header = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.h3};
`;

const AuthSection = styled.section`
  display: flex;
`;

const InfoSection = styled.section`
  margin-top: ${({ theme: { grid } }) => grid.units(2)};
`;

const InfoInner = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${({ theme: { grid } }) => grid.units(1)};
`;

const SubmitButton = styled(Button).attrs(
  // `size` is forwarded to the underlying element as a DOM attribute; it is not
  // part of the typed Button props, so cast to keep the exact runtime value.
  { size: 'large' } as unknown as Partial<React.ComponentProps<typeof Button>>,
)`
  align-self: center;
  margin-top: 20px;
`;

const Input = styled(FjInput).attrs(({ theme }) => ({
  borderColor: theme.colors.black,
  height: parseInt(theme.grid.units(1.5), 10),
}))`
  margin: 5px 0 15px;
`;

// These label spans carry non-standard attributes (bold/fontSize/color/htmlFor)
// that are passed through to the DOM element. They are not valid span props, so
// build the attrs object and cast to preserve the exact runtime markup.
const labelAttrs = (htmlFor: string): React.HTMLAttributes<HTMLSpanElement> =>
  ({
    bold: 'true',
    fontSize: 14,
    color: FjStyles.colors.black.hex(),
    htmlFor,
  }) as unknown as React.HTMLAttributes<HTMLSpanElement>;

const PortPersonalData = (): React.JSX.Element => {
  const [form, setForm] = useState<FormState>({
    acknowledge: false,
    company: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    phone: '',
  });

  const onValueChange = useCallback(
    <K extends keyof FormState>(key: K, value: FormState[K]): void => {
      setForm((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const isDisabled = !validateEmail.validator(form.emailAddress) || !form.acknowledge;

  const onSubmit = useCallback((ev: React.FormEvent<HTMLFormElement>): void => {
    ev.preventDefault();
    // @TODO wire in callback for sending data to Data Protection Officer
    navigate('/');
  }, []);

  return (
    <Modal
      size={fjModalSizes.TALL}
      blocking
      cancel={{
        text: 'Cancel',
        callback: () => navigate('/'),
      }}
      title={<Header>Right to Portability</Header>}
    >
      <form onSubmit={onSubmit} data-test="port-data-form">
        <AuthSection>
          <FjCheckbox
            type="checkbox"
            checked={form.acknowledge}
            name="acknowledge"
            onChange={({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) =>
              onValueChange('acknowledge', checked)
            }
          />
          <p className="margin-0">
            I am authorizing the transfer of my personal data under the &apos;Right to
            Portability&apos; section of the GDPR and understand that my personal data will be sent
            to the person and organization I have identified in the fields below at my request.
          </p>
        </AuthSection>
        <InfoSection className="margin-top-1">
          <Header>Data Protection Officer Information</Header>
          <InfoInner>
            <span {...labelAttrs('company')}>Company or Institution</span>
            <Input
              id="company"
              value={form.company}
              onChange={(val: string) => onValueChange('company', val)}
              placeholder="e.g. FlowJo, LLC"
            />
            <span {...labelAttrs('firstName')}>First Name</span>
            <Input
              id="firstName"
              value={form.firstName}
              onChange={(val: string) => onValueChange('firstName', val)}
            />
            <span {...labelAttrs('lastName')}>Last Name</span>
            <Input
              id="lastName"
              value={form.lastName}
              onChange={(val: string) => onValueChange('lastName', val)}
            />
            <span {...labelAttrs('emailAddress')}>Email Address</span>
            <Input
              id="emailAddress"
              value={form.emailAddress}
              onChange={(val: string) => onValueChange('emailAddress', val)}
              placeholder="example@flowjo.com"
              validator={validateEmail.validator}
              getValidationMessage={validateEmail.getValidationMessage}
            />
            <span {...labelAttrs('phone')}>Phone</span>
            <Input
              id="phone"
              value={form.phone}
              onChange={(val: string) => onValueChange('phone', val)}
              placeholder="(234)-567-8901"
            />
            <SubmitButton disabled={isDisabled}>Submit</SubmitButton>
          </InfoInner>
        </InfoSection>
      </form>
    </Modal>
  );
};

export default PortPersonalData;
